import { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { useFormik, Formik, Field, Form } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { wait } from '../../ulits/wait';
import { getNewUsers, getLocation, getCountryCodes } from '../../fetchApi';

import { userSchema } from '../../schemas';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { Loader } from '../Loader';
import { Snackbar } from '../Snackbar';
import { allCountries } from '../../data/countries';
import { emptyUser } from '../../data/emptyUser';

// import 'bootstrap/dist/css/bootstrap.min.css';
import './Table.scss';
import './UserDetails.scss';

const AddNewUser = (props) => {
  // state hooks
  const { sendSnackbar } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [usersCoords, setUsersCoords] = useState(['0', '0']);
  const [error, setError] = useState(null);
  const [isGettingCoords, setIsGettingCoords] = useState(false);

  const navigate = useNavigate();
  const userId = useParams('id');

  // if (localStorage.getItem('users') === null) {
  //   console.error('localstorage item "users" is null');
  //   sendSnackbar('error', "Couldn't fetch user");
  //   navigate('/');
  //   return;
  // }

  // save all users in a variable
  const storedUsers = JSON.parse(window.localStorage.getItem('users'));

  // our new user will be initially imported from this template
  let user = emptyUser;

  const onSubmit = async (vals, { resetForm }) => {
    user.dob.age = vals.age;
    user.location.city = vals.city;
    user.location.state = vals.state;
    user.location.country = vals.country;
    user.realLocation.country = vals.country;
    user.dob.date = vals.dateOfBirth;
    user.email = vals.email;
    user.name.first = vals.firstName;
    user.gender = vals.gender;
    user.name.last = vals.lastName;
    user.picture.large = vals.picture;
    user.location.postcode = vals.postcode;
    user.location.street.name = vals.streetName;
    user.location.street.number = vals.streetNumber;
    user.name.title = vals.title;
    user.login.username = vals.username;
    user.login.uuid = uuidv4();

    user.realLocation.coords = usersCoords;

    storedUsers.push(user);

    window.localStorage.setItem('users', JSON.stringify(storedUsers));

    await wait(2000);
    sendSnackbar('success', 'User added');

    // reseting values and variables after sending data + navigating back to list of users
    resetForm();
    user = {};
    setUsersCoords(['', '']);
    navigate(-2);
  };

  // * formik setup stuff
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = useFormik({
    initialValues: {
      //! ALL VALUES HERE
      username: '',
      firstName: '',
      lastName: '',
      title: 'mrs',
      email: '',
      gender: 'female',
      age: '',
      dateOfBirth: '',
      country: '',
      city: '',
      streetName: '',
      streetNumber: '',
      postcode: '',
      state: '',
      picture: 'https://randomuser.me/api/portraits/women/40.jpg',
      id: '',
      latitude: `0`,
      longitude: `0`,
    },
    validationSchema: userSchema,
    onSubmit,
  });

  const handleRadioTitle = (e) => {
    values.title = e.target.value;
  };
  const handleRadioGender = (e) => {
    values.gender = e.target.value;
  };
  // randomize img based on actual title (mr or mrs)
  const changeImg = () => {
    const { gender } = values;
    const randomInt = Math.floor(Math.random() * 100);
    let sex = 'women';
    const imgToSwap = document.getElementById('chosenImg');

    if (gender.toLowerCase() === 'male') {
      sex = 'men';
    }
    const imgSrc = `https://randomuser.me/api/portraits/${sex}/${randomInt}.jpg`;
    values.picture = imgSrc;
    imgToSwap.src = imgSrc;
  };

  const handleCoordsChange = (e) => {
    if (e.target.id === 'latitude') {
      setUsersCoords([e.target.value, usersCoords[1]]);
    } else {
      setUsersCoords([usersCoords[0], e.target.value]);
    }
  };
  // randomize coords based on country code (if can't fetch country code, display error)
  const refreshLocation = async (e, country) => {
    e.preventDefault();
    if (!allCountries.includes(country)) {
      setError('Country name is invalid');
      sendSnackbar('error', 'Country name is invalid');
      return;
    }
    setIsGettingCoords(true);
    // check if country name is valid, if so, get its country code
    // * getCountryCodes accepts and returns array!
    const countryCode = getCountryCodes([country])[0];

    // we have code so we can fetch coords now
    const newCoords = await getLocation(countryCode);

    setUsersCoords(newCoords);
    setIsGettingCoords(false);
  };

  return (
    <div className="user-details">
      <h2>Edit User Page</h2>
      <div className="user-details-container">
        <form autoComplete="off" onSubmit={(e) => handleSubmit(e)}>
          <header className="user-header">
            <Button
              className="img-randomizable"
              type="button"
              onClick={changeImg}
              onKeyDown={changeImg}
            >
              <img id="chosenImg" src={values.picture} alt="" />
            </Button>
          </header>
          <Table striped bordered hover variant="dark">
            <thead>
              <tr>
                <th>Property</th>
                <th colSpan={3}>Value</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>Username</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.username}
                    id="username"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.username && touched.username ? 'input-error' : ''
                    }
                  />
                  {errors.username && touched.username ? (
                    <span className="error">{errors.username}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>First name</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.firstName}
                    id="firstName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.firstName && touched.firstName ? 'input-error' : ''
                    }
                  />
                  {errors.firstName && touched.firstName ? (
                    <span className="error">{errors.firstName}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Last name</td>
                <td className="inputTd">
                  <input
                    autoComplete="off"
                    type="text"
                    value={values.lastName}
                    id="lastName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.lastName && touched.lastName ? 'input-error' : ''
                    }
                  />
                  {errors.lastName && touched.lastName ? (
                    <span className="error">{errors.lastName}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Title</td>
                <td className="inputTd">
                  <fieldset>
                    <input
                      type="radio"
                      value="mr"
                      name="title"
                      id="mr"
                      onChange={(e) => handleRadioTitle(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.title.toLowerCase() === 'mr' ? 'checked' : ''
                      }
                      className={
                        errors.title && touched.title ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="mr">Mr</label>

                    <input
                      type="radio"
                      value="mrs"
                      name="title"
                      id="mrs"
                      onChange={(e) => handleRadioTitle(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.title.toLowerCase() === 'mrs' ? 'checked' : ''
                      }
                      className={
                        errors.title && touched.title ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="mrs">Mrs</label>
                  </fieldset>

                  {errors.title && touched.title ? (
                    <span className="error">{errors.title}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Email</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.email}
                    id="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.email && touched.email ? 'input-error' : ''
                    }
                  />
                  {errors.email && touched.email ? (
                    <span className="error">{errors.email}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Gender</td>
                <td className="inputTd">
                  <fieldset>
                    <input
                      type="radio"
                      value="male"
                      name="gender"
                      id="male"
                      onChange={(e) => handleRadioGender(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.gender.toLowerCase() === 'male' ? 'checked' : ''
                      }
                      className={
                        errors.gender && touched.gender ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="male">Male</label>

                    <input
                      type="radio"
                      value="female"
                      name="gender"
                      id="female"
                      onChange={(e) => handleRadioGender(e)}
                      onBlur={handleBlur}
                      defaultChecked={
                        values.gender.toLowerCase() === 'female'
                          ? 'checked'
                          : ''
                      }
                      className={
                        errors.gender && touched.gender ? 'input-error' : ''
                      }
                    />
                    <label htmlFor="female">Female</label>
                  </fieldset>
                </td>
              </tr>
              <tr>
                <td>Birthdate</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.dateOfBirth.slice(0, 10)}
                    id="dateOfBirth"
                    onChange={handleChange}
                    placeholder="dd-mm-yyyy"
                    onBlur={handleBlur}
                    className={
                      errors.dateOfBirth && touched.dateOfBirth
                        ? 'input-error'
                        : ''
                    }
                  />
                  {errors.dateOfBirth && touched.dateOfBirth ? (
                    <span className="error">{errors.dateOfBirth}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Age</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.age}
                    id="age"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.age && touched.age ? 'input-error' : ''}
                  />
                  {errors.age && touched.age ? (
                    <span className="error">{errors.age}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              {/* // ! Address */}
              <tr>
                <td colSpan="2">
                  <b>Address</b>
                </td>
              </tr>
              <tr>
                <td>Street Name</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.streetName}
                    id="streetName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.streetName && touched.streetName
                        ? 'input-error'
                        : ''
                    }
                  />
                  {errors.streetName && touched.streetName ? (
                    <span className="error">{errors.streetName}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Street Number</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.streetNumber}
                    id="streetNumber"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.streetNumber && touched.streetNumber
                        ? 'input-error'
                        : ''
                    }
                  />
                  {errors.streetNumber && touched.streetNumber ? (
                    <span className="error">{errors.streetNumber}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>State</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.state}
                    id="state"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.state && touched.state ? 'input-error' : ''
                    }
                  />
                  {errors.state && touched.state ? (
                    <span className="error">{errors.state}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>

              <tr>
                <td>City</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.city}
                    id="city"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.city && touched.city ? 'input-error' : ''}
                  />
                  {errors.city && touched.city ? (
                    <span className="error">{errors.city}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>Postcode</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.postcode}
                    id="postcode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.postcode && touched.postcode ? 'input-error' : ''
                    }
                  />
                  {errors.postcode && touched.postcode && (
                    <span className="error">{errors.postcode}</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Country</td>
                <td className="inputTd">
                  <input
                    type="text"
                    value={values.country}
                    id="country"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.country && touched.country ? 'input-error' : ''
                    }
                  />
                  {errors.country && touched.country ? (
                    <span className="error">{errors.country}</span>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan="2">
                  <div className="geolocation">
                    <b>Geolocation (based on country)</b>
                    {isGettingCoords ? (
                      <Loader msg="" />
                    ) : (
                      <Button
                        onClick={(e) => refreshLocation(e, values.country)}
                      >
                        🔄
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Latitude</td>
                <td className="inputTd">
                  <input
                    type="text"
                    id="latitude"
                    value={usersCoords[0]}
                    onChange={handleCoordsChange}
                    onBlur={handleBlur}
                    className={
                      errors.postcode && touched.postcode ? 'input-error' : ''
                    }
                  />
                </td>
              </tr>
              <tr>
                <td>Longitude</td>
                <td className="inputTd">
                  <input
                    type="text"
                    id="longitude"
                    value={usersCoords[1]}
                    onChange={handleCoordsChange}
                    onBlur={handleBlur}
                    className={
                      errors.postcode && touched.postcode ? 'input-error' : ''
                    }
                  />
                </td>
              </tr>
              <tr />
            </tbody>
          </Table>

          {isSubmitting ? (
            <Loader msg="Adding user" />
          ) : (
            <div className="edit-user-buttons">
              <Button theme="dark" type="button" onClick={() => navigate(-1)}>
                Discard
              </Button>
              <Button theme="dark" type="submit">
                Add user
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddNewUser;
